import { Arg, Ctx, Directive, Mutation, Resolver } from 'type-graphql'
import { In } from 'typeorm'

import { Setting } from './setting'
import { NewSetting, SettingPatch } from './setting-type'

@Resolver(Setting)
export class SettingMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "setting", privilege: "mutation")')
  @Mutation(returns => Setting, { description: 'To create new Setting' })
  async createSetting(@Arg('setting') setting: NewSetting, @Ctx() context: ResolverContext): Promise<Setting> {
    const { tx, domain, user } = context.state
    return await tx.getRepository(Setting).save({
      domain,
      ...setting,
      creator: user,
      updater: user
    })
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "setting", privilege: "mutation")')
  @Mutation(returns => Setting, { description: 'To modify Setting information' })
  async updateSetting(
    @Arg('name') name: string,
    @Arg('patch') patch: SettingPatch,
    @Ctx() context: ResolverContext
  ): Promise<Setting> {
    const { tx, domain, user } = context.state

    const repository = tx.getRepository(Setting)
    const setting = await repository.findOneBy({ domain: { id: domain.id }, name })

    return await repository.save({
      name,
      creater: user,
      ...setting,
      ...patch,
      domain,
      updater: user
    })
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "setting", privilege: "mutation")')
  @Mutation(returns => [Setting], { description: "To modify multiple Settings' information" })
  async updateMultipleSetting(
    @Arg('patches', type => [SettingPatch]) patches: SettingPatch[],
    @Ctx() context: ResolverContext
  ): Promise<Setting[]> {
    const { tx, domain, user } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')
    const settingRepo = tx.getRepository(Setting)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        const result = await settingRepo.save({
          domain: { id: domain.id },
          creator: user,
          updater: user,
          ...newRecord
        })

        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const newRecord = _updateRecords[i]
        const setting = await settingRepo.findOneBy({ id: newRecord.id })

        const result = await settingRepo.save({
          ...setting,
          ...newRecord,
          updater: user
        })

        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "setting", privilege: "mutation")')
  @Mutation(returns => Boolean, { description: 'To delete Setting' })
  async deleteSetting(@Arg('name') name: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { tx, domain } = context.state

    await tx.getRepository(Setting).delete({ domain: { id: domain.id }, name })
    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "setting", privilege: "mutation")')
  @Mutation(returns => Boolean, { description: 'To delete multiple settings' })
  async deleteSettings(
    @Arg('names', type => [String]) names: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { tx, domain } = context.state

    await tx.getRepository(Setting).delete({
      domain: { id: domain.id },
      name: In(names)
    })

    return true
  }
}
